import { type ReactNode, useEffect, useRef, useState } from "react";

import { KeycloakContext } from "@/components/keycloak/KeycloakContext";
import { env } from "@/env";
import { getKeycloakInstance } from "@/lib/auth/keycloak";
import * as Sentry from "@sentry/react";

import { SENTRY_ERROR_TYPE, SENTRY_SEVERITY } from "@workspace/schemas/errors";

export function KeycloakProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keycloak = getKeycloakInstance();

  // Prevent double initialization in Strict Mode
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    // Prevent state updates after unmount
    let isMounted = true;

    keycloak
      .init({
        onLoad: "login-required", // Force login for all users
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .then((auth) => {
        if (!isMounted) return;
        setAuthenticated(auth);
        setInitialized(true);

        // Set Sentry user context for error tracking
        if (auth && keycloak.tokenParsed) {
          Sentry.setUser({
            id: keycloak.tokenParsed.sub,
            username: keycloak.tokenParsed.preferred_username as string | undefined,
          });
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        // Capture auth failures to Sentry (critical errors)
        Sentry.captureException(error, {
          tags: {
            severity: SENTRY_SEVERITY.CRITICAL,
            errorType: SENTRY_ERROR_TYPE.AUTH_INIT_FAILURE,
            envFilter: env.VITE_ENVIRONMENT,
          },
        });
        // eslint-disable-next-line no-console
        console.error("Keycloak initialization failed", error);
        setError("Anmeldung fehlgeschlagen");
        setInitialized(true);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div>
        <h2>Anmeldefehler</h2>
        <p>{error ?? "Anmeldung fehlgeschlagen"}</p>
      </div>
    );
  }

  if (!initialized) {
    return null;
  }

  if (!authenticated) {
    return <div>Weiterleitung zum Login...</div>;
  }

  return <KeycloakContext.Provider value={{ keycloak, initialized, authenticated }}>{children}</KeycloakContext.Provider>;
}
